import React, { useState, useEffect } from 'react';
import { X, Save, Loader, Pill, Search } from 'lucide-react';
import ApiService from './ApiService';

const MedicationAssignment = ({ isOpen, onClose, patient, config, onMedicationAssigned }) => {
  const [medications, setMedications] = useState([]);
  const [patientMedications, setPatientMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [formData, setFormData] = useState({
    notes: ''
  });

  const t = config?.terminology || {
    item: { singular: 'Medication', plural: 'Medications', singularLower: 'medication', pluralLower: 'medications' }
  };

  useEffect(() => {
    if (isOpen) {
      loadMedications();
    }
  }, [isOpen]);

  const loadMedications = async () => {
    try {
      setLoading(true);
      const [allMeds, patientMeds] = await Promise.all([
        ApiService.getMedications(),
        ApiService.getPatientMedications(patient.patientId)
      ]);
      setMedications(allMeds);
      setPatientMedications(patientMeds);
    } catch (err) {
      setError('Failed to load medications');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter out already assigned medications
  const availableMedications = medications.filter(med => 
    !patientMedications.some(pm => pm.medicationId === med.medicationId)
  );

  const handleSelectMedication = (medication) => {
    setSelectedMedication(medication);
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedMedication) {
      setError('Please select a medication');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Build assignment data with scheduling fields
      const assignmentData = {
        medicationId: selectedMedication.medicationId,
        medicationName: selectedMedication.name
      };

      // Only add notes if they exist and aren't empty
      const trimmedNotes = formData.notes?.trim();
      if (trimmedNotes && trimmedNotes.length > 0) {
        assignmentData.notes = trimmedNotes;
      }

      // Remove any undefined or empty values before sending
      Object.keys(assignmentData).forEach(key => {
        if (assignmentData[key] === undefined || assignmentData[key] === '') {
          delete assignmentData[key];
        }
      });

      console.log('Sending assignment data:', assignmentData); // Debug log

      await ApiService.addMedicationToPatient(patient.patientId, assignmentData);
      onMedicationAssigned();
      handleClose();
    } catch (err) {
      setError('Failed to assign medication: ' + (err.message || 'Unknown error'));
      console.error('Assignment error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedMedication(null);
    setFormData({
      notes: ''
    });
    setSearchTerm('');
    setError('');
    onClose();
  };

  const filteredMedications = availableMedications.filter(med =>
    med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (med.category && med.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-spruce-800 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
              <Pill className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                Assign {t.item.singular}
              </h2>
              <p className="text-spruce-100 text-sm">
                {patient.firstName} {patient.lastName}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleAssign} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Select Medication */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Select {t.item.singular} <span className="text-red-500">*</span>
              </label>

              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={`Search ${t.item.pluralLower}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                />
              </div>

              {/* Medication List */}
              <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader className="w-6 h-6 text-spruce-600 animate-spin" />
                  </div>
                ) : filteredMedications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No {t.item.pluralLower} found
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredMedications.map((med) => (
                      <button
                        key={med.medicationId}
                        type="button"
                        onClick={() => handleSelectMedication(med)}
                        className={`w-full px-4 py-3 text-left hover:bg-spruce-50 transition-colors ${
                          selectedMedication?.medicationId === med.medicationId
                            ? 'bg-spruce-100 border-l-4 border-spruce-600'
                            : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">{med.name}</span>
                              {selectedMedication?.medicationId === med.medicationId && (
                                <span className="text-spruce-600">✓</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              {med.category && (
                                <span className="inline-block px-2 py-0.5 bg-spruce-100 text-spruce-700 text-xs rounded-full">
                                  {med.category}
                                </span>
                              )}
                              {med.dosageUnit && (
                                <span className="text-sm text-gray-500">
                                  Measured in {med.dosageUnit}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Chart entry only: dosage, timing, and reminders come from
                creating a protocol for this medication afterward */}
            {selectedMedication && (
              <div className="bg-mist border border-line rounded-lg px-4 py-3 text-sm text-gray-600">
                This adds <strong>{selectedMedication.name}</strong> to the patient's chart.
                To set dosage, schedule, and enable reminders, create a protocol for it under the Protocols tab.
              </div>
            )}

            {/* Notes (Optional) */}
            {selectedMedication && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Instructions / Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500 focus:border-transparent"
                  placeholder="e.g., Take with food, Take before bedtime..."
                  rows="3"
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-3 justify-end mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedMedication || saving}
              className="px-6 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Assign {t.item.singular}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MedicationAssignment;